import { makeStyles, Spinner } from "@fluentui/react-components";
import { AirplaneFilled } from "@fluentui/react-icons";
import React, { useContext, useEffect, useState } from "react";
import { LayoutContext } from "../../../../context/LayoutContext";
import { $cloud100, $white } from "../../../../cssjs/colors";
import { useApiCall } from "../../../../hooks/useApiCall";
import {
  IFlightSummaryKpi,
  IFlightSummaryResponse,
} from "../../../../models/flights/IFlightSummary";
import { CommonHelper } from "../../../../utils/common/CommonHelper";
import { EMPTY_STATE_MESSAGE } from "../../../../utils/common/Constants";
import {
  buildFlightSummaryPayload,
  getFlightSummaryResult,
} from "../../../../utils/flights/FlightSummaryApi";
import { FlightSummaryHelper } from "../../../../utils/flights/FlightSummaryHelper";
import { NoDataCard } from "../../../common/no-data-card/NoDataCard";
import { DonutCard } from "./shared/DonutCard";

interface IProps {
  isCarousel?: boolean;
  showFlightDetails?: boolean;
  airportCode: string;
}

export const FlightCard: React.FunctionComponent<IProps> = ({
  isCarousel = false,
  showFlightDetails = false,
  airportCode,
}): React.JSX.Element => {
  const classes = useStyles();
  const { timezone, globalFilterDate } = useContext(LayoutContext);

  const [flightData, setFlightData] = useState<{
    arrival: IFlightSummaryKpi | undefined;
    overall: number | undefined;
    departure: IFlightSummaryKpi | undefined;
  }>({
    arrival: undefined,
    overall: undefined,
    departure: undefined,
  });

  const getFlightKpiUrl = `${process.env.REACT_APP_API_FLIGHTSUMMARY?.replace(
    "{airportCode}",
    airportCode
  )}`;

  const payload = buildFlightSummaryPayload(
    airportCode,
    globalFilterDate.startDate,
    globalFilterDate.endDate,
    "Best",
    timezone
  );

  const {
    data: getFlightKpiResponses,
    error,
    isLoading,
  } = useApiCall<IFlightSummaryResponse[]>({
    requestType: "post",
    url: getFlightKpiUrl,
    payload,
    dependencies: [airportCode, timezone],
    showLoaderOnFirstCallOnly: false,
    showLoaderOnDependencyChangeOnly: true,
  });

  let resourceData = undefined;

  useEffect(() => {
    const newFlightData: {
      arrival: IFlightSummaryKpi | undefined;
      overall: number | undefined;
      departure: IFlightSummaryKpi | undefined;
    } = {
      arrival: undefined,
      overall: undefined,
      departure: undefined,
    };

    if (getFlightKpiResponses) {
      getFlightSummaryResult(getFlightKpiResponses, airportCode).forEach(
        (item) => {
          if (item.type === "Overall") {
            newFlightData.overall = item.kpiValue;
          } else if (item.type === "Arrivals") {
            newFlightData.arrival = item;
          } else if (item.type === "Departures") {
            newFlightData.departure = item;
          }
        }
      );
    }

    setFlightData(newFlightData);
  }, [airportCode, getFlightKpiResponses]);

  if (getFlightKpiResponses) {
    resourceData = {
      key: "flightsCard",
      title: CommonHelper.numberFomatter(
        flightData.overall ? flightData.overall : 0
      ),
      subTitle: "Flights",
      data: {
        arrivals: FlightSummaryHelper.getDonutChartData(flightData?.arrival),
        departures: FlightSummaryHelper.getDonutChartData(
          flightData?.departure
        ),
      },
    };
  }

  return (
    <div
      className={showFlightDetails ? classes.rootHidden : undefined}
      data-testid="flight-card"
    >
      {error && !isLoading && (
        <NoDataCard message={EMPTY_STATE_MESSAGE.Flights} />
      )}
      {isLoading ? (
        <Spinner
          labelPosition="below"
          label="Loading..."
          className={`${
            isCarousel ? classes.carouselCardLoader : classes.cardLoader
          }`}
        />
      ) : null}
      {!error && !isLoading ? (
        <div
          className={`${classes.cardContainer} ${
            isCarousel
              ? classes.carouselCardContainer
              : classes.defaultCardContainer
          }`}
        >
          {resourceData && (
            <DonutCard data={resourceData} isCarousel={isCarousel}>
              <AirplaneFilled />
            </DonutCard>
          )}
        </div>
      ) : null}
    </div>
  );
};

const useStyles = makeStyles({
  rootHidden: {
    visibility: "hidden",
  },
  cardLoader: {
    height: "100%",
    minHeight: "173px",
  },
  carouselCardLoader: {
    height: "100%",
    background: $white,
    maxHeight: "203px",
    minHeight: "203px",
  },
  cardContainer: {
    display: "grid",
    gridGap: "8px",
    gridTemplateColumns: "auto 1fr ",
    gridTemplateRows: "42px 0px auto",
    boxSizing: "border-box",
    background: $white,
    color: $cloud100,
  },
  carouselCardContainer: {
    border: "none",
    borderRadius: "none",
    maxHeight: "203px",
    padding: "16px 57px",
    margin: "0 auto 11px auto",
  },
  defaultCardContainer: {
    borderRadius: "8px",
    padding: "8px",
    minHeight: "100%",
  },
});
